import { DISPATCH_ACTION, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { useCallback, useEffect, useMemo } from 'react'
import type UserCartStore from '../../UserData/_stores/UserCartStore'
import type Immutable from 'immutable'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import SkeletonLoader from '../../Common/Loader/SkeletonLoader'
import type { AjaxResponse } from '../../Common/_actions/AjaxAction'
import type { CreateOrderData, CreateOrderActions, OnApproveData, OnApproveActions } from '@paypal/paypal-js'
import type { ButtonProps } from '@mui/material'
import type { UserCartPayload } from '../../UserData/_stores/UserCartStore'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { EditManyOrders } from '../../UserData/_actions/OrderStoreActions'
import { getMetaCheckoutEventId, queueMetaServerEvent, trackMetaBrowserEvent } from '../../Common/Tracking/MetaConversions'
import { queueOpenAIAdsServerEvent, trackOpenAIAdsPaymentInfoAdded } from '../../Common/Tracking/OpenAIAdsTracking'

const PAYPAL_INSTRUMENT_DECLINED = 'INSTRUMENT_DECLINED'
const PAYPAL_EMPTY_CART = 'empty-cart'

type Props = ButtonProps & {
  orders: Immutable.Map<string, UserCartStore>
  confirmationUrl: string
  onErrorText: (error: string) => void
  onDisable: (disable: boolean) => void
}

export default function PayPalPaymentButton(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('cart')
  const navigate = useNavigate()
  const [{ isPending: paypalIsPending, options: paypalOptions }, paypalDispatch] = usePayPalScriptReducer()

  const createOrder = useCallback(async (data: CreateOrderData, actions: CreateOrderActions) => {
    props.onDisable(true)
    props.onErrorText('')

    // Provide id order if we're paying an 'ordered' order
    let idOrder: number | null = null
    if (props.orders.count() === 1 && props.orders.first().get('status') === 'ordered') {
      idOrder = props.orders.first().get('id')
    }

    props.orders.forEach(cart => {
      const eventId = getMetaCheckoutEventId('AddPaymentInfo', cart, 'paypal')
      trackMetaBrowserEvent('AddPaymentInfo', cart, eventId)
      queueMetaServerEvent('AddPaymentInfo', cart, eventId, 'paypal')
      trackOpenAIAdsPaymentInfoAdded(cart, eventId)
      queueOpenAIAdsServerEvent('payment_info_added', cart, eventId)
    })

    const fd = new FormData()
    if (idOrder !== null) {
      fd.append('id_order', String(idOrder))
    }

    const controller = new AbortController()

    return await fetch('ajax/checkout/paypal-v2/create-order', {
      method: 'POST',
      body: fd,
      signal: controller.signal
    })
      .then(async response => {
        if (!response.ok) {
          if (response.status === 400) {
            const data = await response.json() as { data?: { error: string | undefined } } | undefined
            if (data?.data?.error) {
              throw new Error(data.data.error)
            }
          }

          throw new Error('An error occurred')
        }
        return response
      })
      .then(async response => await response.json())
      .then((response: AjaxResponse<{ id: string }>) => response.data.id)
  }, [props.onDisable, props.onErrorText, props.orders])

  const onApprove = useCallback(async (data: OnApproveData, actions: OnApproveActions) => {
    props.onDisable(true)
    props.onErrorText('')

    const fd = new FormData()
    fd.append('id', data.orderID)
    fd.append('confirmation_url', props.confirmationUrl)

    const controller = new AbortController()

    try {
      const response = await fetch('ajax/checkout/paypal-v2/on-approve', {
        method: 'POST',
        signal: controller.signal,
        body: fd
      })

      if (!response.ok) {
        if (response.status === 400) {
          const data = await response.json() as AjaxResponse<{ error?: string }>
          if (data.data.error === PAYPAL_INSTRUMENT_DECLINED) {
            props.onDisable(false)
            actions.restart()
            return
          }
        }

        throw new Error('An error occurred')
      }

      const responseData = await response.json() as AjaxResponse<{ payment_id: string, orders: Record<string, UserCartPayload> }>
      dispatch(EditManyOrders(responseData.data.orders))
      void navigate(props.confirmationUrl + '?payment_id=' + encodeURIComponent(responseData.data.payment_id))
    } catch {
      props.onDisable(false)
      props.onErrorText(t('An error occured processing your order. Please try again later.'))
    }
  }, [dispatch, navigate, t, props.confirmationUrl, props.onDisable, props.onErrorText])

  const onCancel = useCallback(() => {
    props.onDisable(false)
    props.onErrorText('')
  }, [props.onDisable, props.onErrorText])

  const onError = useCallback((error: Record<string, unknown>) => {
    props.onDisable(false)

    if (error.message === 'invalid-shipping-address') {
      props.onErrorText(t('Your shipping address is invalid. Please edit and reconfirm your address.'))
    } else if (error.message === 'invalid-billing-address') {
      props.onErrorText(t('Your billing address is invalid. Please edit reconfirm your address.'))
    } else if (error.message === PAYPAL_EMPTY_CART) {
      props.onErrorText(t('Your cart is empty.'))
    } else {
      props.onErrorText(t('An error occured processing your order with PayPal. Please try again later.'))
    }

    console.log('PayPal onError', error)
  }, [t, props.onDisable, props.onErrorText])

  const firstOrder = useMemo(() => {
    return props.orders.first()
  }, [props.orders])

  const currency = useMemo(() => {
    if (!firstOrder) return 'USD'

    return firstOrder.get('total').get('currency')
  }, [firstOrder])

  useEffect(() => {
    // Trigger paypal update on currency change
    paypalDispatch({
      type: DISPATCH_ACTION.RESET_OPTIONS,
      value: {
        ...paypalOptions,
        currency
      }
    })
  }, [currency])

  return <SkeletonLoader
    loading={paypalIsPending}
    variant="rounded"
    height={42}
    width={150}
  >
    <PayPalButtons
      createOrder={createOrder}
      onApprove={onApprove}
      onCancel={onCancel}
      onError={onError}
      disabled={props.disabled}
      style={{
        shape: 'pill',
        color: 'gold',
        label: 'paypal',
        tagline: false,
        layout: 'vertical',
        height: 42
      }}
    />
  </SkeletonLoader>
}
